import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';

import { VendorEvaluationModal } from './VendorEvaluationModal.jsx';

const GRADE_VARIANT = { A: 'success', B: 'info', C: 'warning', D: 'danger' };

export function VendorEvaluationPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);

  const ranking = useQuery({ queryKey: ['inventory', 'supplier-ranking'], queryFn: () => inventoryApi.supplierRanking().then((r) => r.items) });
  const evals = useQuery({ queryKey: ['inventory', 'supplier-evaluations'], queryFn: () => inventoryApi.listSupplierEvaluations().then((r) => r.items) });

  const rankColumns = [
    { key: 'rank', label: '#', render: (_r, i) => <span className="muted">{i + 1}</span> },
    { key: 'supplier', label: t('ve.col.supplier'), render: (r) => <span><span className="mono"><strong>{r.code}</strong></span> {r.name}</span> },
    { key: 'avg', label: t('ve.col.avg'), align: 'right', render: (r) => <span className="mono num">{Number(r.avgScore).toFixed(2)}</span> },
    { key: 'grade', label: t('ve.col.grade'), render: (r) => <Badge variant={GRADE_VARIANT[r.grade]}>{r.grade}</Badge> },
    { key: 'latest', label: t('ve.col.latest'), align: 'right', render: (r) => <span className="mono num">{Number(r.latestScore).toFixed(2)}</span> },
    { key: 'count', label: t('ve.col.count'), align: 'right', render: (r) => r.evaluations },
  ];

  const evalColumns = [
    { key: 'date', label: t('ve.col.date'), render: (r) => formatDate(r.evaluationDate) },
    { key: 'supplier', label: t('ve.col.supplier'), render: (r) => r.supplierName },
    { key: 'price', label: t('ve.col.price'), align: 'right', render: (r) => <span className="mono num">{r.priceScore}</span> },
    { key: 'quality', label: t('ve.col.quality'), align: 'right', render: (r) => <span className="mono num">{r.qualityScore}</span> },
    { key: 'delivery', label: t('ve.col.delivery'), align: 'right', render: (r) => <span className="mono num">{r.deliveryScore}</span> },
    { key: 'service', label: t('ve.col.service'), align: 'right', render: (r) => <span className="mono num">{r.serviceScore}</span> },
    { key: 'overall', label: t('ve.col.overall'), align: 'right', render: (r) => <span className="mono num"><strong>{Number(r.overallScore).toFixed(2)}</strong></span> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('ve.title')}</h1>
          <p className="muted">{t('ve.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('ve.action.new')}</Button>
        </div>
      </header>

      <div className="settings-divider">{t('ve.ranking')}</div>
      <Table columns={rankColumns} rows={ranking.data || []} isLoading={ranking.isLoading} emptyMessage={t('ve.noRanking')} />

      <div className="settings-divider">{t('ve.evaluations')}</div>
      <Table columns={evalColumns} rows={evals.data || []} isLoading={evals.isLoading} emptyMessage={t('ve.empty')} />

      <VendorEvaluationModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
